
import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { cloneDeep } from 'lodash';
import { Contract } from '../../customProvider/objects/contract';
import { Accommodation } from '../objects/accommodation';
import { Price } from '../objects/price';
import { PriceTypes } from '../objects/priceType';
import { Room } from '../objects/room';
import { RoomProduct } from '../objects/roomProduct';
import { RoomProductInfo } from '../objects/roomProductInfo';
import { StockData } from '../objects/stockData';
import { StockStatusState } from '../objects/stockStatusState';
import { getDates } from '../utils/getDate';
import { EventSiteDate } from '../../customProvider/objects/eventSiteDate';

const initialState: StockStatusState = {
    accommodation_list: null,
    accommodation_list_loading: false,
    contract_list: null,
    room_list: null,
    price_list: null,
    closed_sale_hover_date: null,
    closed_sale_start_date: null,
    closed_sale_end_date: null,
    closed_sale_room_id: null,
    closed_sale_accommodation_id: null,
    current_day: null,
    current_month: null,
    display_mode: 0,
    edit_mode: null,
    end_date: null,
    start_date: null,
    stock_room_id: null,
    stock_accommodation_id: null,
    stock_index: null,
    stock_price_id: null,
    stock_contract_id: null,
    stock_value: '',
    stock_date: null,
    price_room_id: null,
    price_accommodation_id: null,
    price_price_id: null,
    price_contract_id: null,
    price_value: '',
    price_date: null,
    price_type: null,
    price_row_index: null,
    price_column_index: null,
    is_modal_open: false,
    dates: null,
    no_active_contract: false,
    no_active_contract_contract: null,
    no_active_contract_date: null,
    stock_status_data: null,
    stock_status_data_row_length: 0,
    stock_status_data_column_length: 0,
    room_products: null,
    room_product_infos: null,
    room_products_loading: false,
    refresh_list: false
};

const noActiveContractForDateReducer = (state: StockStatusState, action: PayloadAction<{ date: Date | null, contract: Contract | null }>): StockStatusState => ({
    ...state,
    no_active_contract: true,
    no_active_contract_contract: action.payload.contract,
    no_active_contract_date: action.payload.date,
    closed_sale_hover_date: null,
    closed_sale_start_date: null,
    closed_sale_end_date: null,
    closed_sale_room_id: null,
    closed_sale_accommodation_id: null,
});

const setNoActiveContractReducer = (state: StockStatusState, action: PayloadAction<boolean>): StockStatusState => ({
    ...state,
    no_active_contract: action.payload,
    no_active_contract_date: action.payload ? state.no_active_contract_date : null,
});

const setAccommodationListReducer = (state: StockStatusState, action: PayloadAction<null | Record<number, Accommodation>>): StockStatusState => ({
    ...state,
    accommodation_list: action.payload
});

const setAccommodationListLoadingReducer = (state: StockStatusState, action: PayloadAction<boolean>): StockStatusState => ({
    ...state, accommodation_list_loading: action.payload
});

const setContractListReducer = (state: StockStatusState, action: PayloadAction<null | Record<number, Contract>>): StockStatusState => ({
    ...state, contract_list: action.payload
});

const setCurrentDayReducer = (state: StockStatusState, action: PayloadAction<null | Date>): StockStatusState => ({
    ...state, current_day: action.payload
});

const setCurrentMonthReducer = (state: StockStatusState, action: PayloadAction<null | Date>): StockStatusState => ({
    ...state, current_month: action.payload
});

const setDateListReducer = (state: StockStatusState, action: PayloadAction<{ start_date: Date, end_date: Date } | null>): StockStatusState => ({
    ...state, dates: action.payload ? getDates(action.payload.start_date, action.payload.end_date) : null
});

const setEditModeReducer = (state: StockStatusState, action: PayloadAction<null | 'STOCK' | 'ROOM_BLACK_OUTS'>): StockStatusState => ({
    ...state, edit_mode: action.payload
});

const setEndDateReducer = (state: StockStatusState, action: PayloadAction<null | Date>): StockStatusState => ({
    ...state, end_date: action.payload
});

const setRoomListReducer = (state: StockStatusState, action: PayloadAction<null | Record<number, Room>>): StockStatusState => ({
    ...state, room_list: action.payload
});

const setPriceListReducer = (state: StockStatusState, action: PayloadAction<null | Record<number, { id: number } & any>>): StockStatusState => ({
    ...state, price_list: action.payload
});

const setStartDateReducer = (state: StockStatusState, action: PayloadAction<null | Date>): StockStatusState => ({
    ...state, start_date: action.payload, current_month: action.payload
});

const setIsModalOpenReducer = (state: StockStatusState, action: PayloadAction<boolean>): StockStatusState => ({
    ...state, is_modal_open: action.payload
});

const toggleDisplayModeReducer = (state: StockStatusState): StockStatusState => ({
    ...state, display_mode: + !state.display_mode as 0 | 1
});

const toggleRefreshListReducer = (state: StockStatusState): StockStatusState => ({
    ...state, refresh_list: !state.refresh_list
});

const updateClosedHoverDateReducer = (state: StockStatusState, action: PayloadAction<number | null>): StockStatusState => ({
    ...state, closed_sale_hover_date: action.payload
});

const updateClosedSaleReducer = (state: StockStatusState, action: PayloadAction<{ accommodation_id: number, room_id: number, date: number }>): StockStatusState => {
    const { accommodation_id, room_id, date } = action.payload;
    if (state.closed_sale_start_date === null) {
        return {
            ...state,
            closed_sale_accommodation_id: accommodation_id,
            closed_sale_room_id: room_id,
            closed_sale_start_date: date
        };
    } else if (state.closed_sale_room_id === room_id && state.closed_sale_accommodation_id === accommodation_id) {
        return {
            ...state,
            closed_sale_room_id: room_id,
            closed_sale_end_date: date
        };
    }
    return state;
};

const updateRoomBlackOutReducer = (state: StockStatusState, action: PayloadAction<Room>): StockStatusState => {
    const new_room_list = cloneDeep(state.room_list);
    if (state.closed_sale_room_id && new_room_list) {
        new_room_list[state.closed_sale_room_id] = { ...new_room_list[state.closed_sale_room_id], black_out: action.payload.black_out };
    }
    return ({
        ...state,
        room_list: new_room_list,
        closed_sale_start_date: null,
        closed_sale_end_date: null,
        closed_sale_room_id: null,
        closed_sale_hover_date: null,
        closed_sale_accommodation_id: null
    });
};

const updatePriceStockReducer = (state: StockStatusState, action: PayloadAction<Price[]>): StockStatusState => ({
    ...state,
    price_list: { ...state.price_list, ...action.payload.map((price) => ({ [price.id as number]: price })) },
    price_room_id: null,
    price_accommodation_id: null,
    price_price_id: null,
    price_value: '',
    price_contract_id: null,
    price_date: null,
    price_type: null,
    price_row_index: null,
    price_column_index: null,
});

const updateRoomStockReducer = (state: StockStatusState, action: PayloadAction<Price[]>): StockStatusState => {
    const price_list = cloneDeep(state.price_list);
    if (state.stock_price_id && price_list && action.payload[0]) {
        price_list[state.stock_price_id] = action.payload[0];
    }
    return {
        ...state,
        stock_room_id: null,
        stock_accommodation_id: null,
        stock_index: null,
        stock_price_id: null,
        stock_value: '',
        stock_contract_id: null,
        stock_date: null,
        price_list: price_list,
    };
};

const updateRoomStockWithoutCloseReducer = (state: StockStatusState, action: PayloadAction<Price[]>): StockStatusState => {
    const price_list = cloneDeep(state.price_list);
    if (state.stock_price_id && price_list && action.payload[0]) {
        price_list[state.stock_price_id] = action.payload[0];
    }
    return {
        ...state,
        price_list: price_list,
    };
};

const updatePriceReducer = (state: StockStatusState, action: PayloadAction<{ accommodation_id: number | null, room_id: null | number, price_id: null | number, price_type: null | PriceTypes, price_value: string | number, contract_id: number | null, price_date: null | Date, row_index: null | number, column_index: null | number }>): StockStatusState => ({
    ...state,
    price_room_id: action.payload.room_id,
    price_accommodation_id: action.payload.accommodation_id,
    price_price_id: action.payload.price_id,
    price_value: action.payload.price_value,
    price_date: action.payload.price_date,
    price_contract_id: action.payload.contract_id,
    price_type: action.payload.price_type,
    price_row_index: action.payload.row_index,
    price_column_index: action.payload.column_index,
});

const updatePriceValueReducer = (state: StockStatusState, action: PayloadAction<string | number>): StockStatusState => ({
    ...state,
    price_value: action.payload
});

const updateStockReducer = (state: StockStatusState, action: PayloadAction<{ accommodation_id: number | null, room_id: null | number, price_id: null | number, index: null | number, stock_value: string | number, contract_id: number | null, stock_date: null | Date }>): StockStatusState => ({
    ...state,
    stock_room_id: action.payload.room_id,
    stock_accommodation_id: action.payload.accommodation_id,
    stock_index: action.payload.index,
    stock_price_id: action.payload.price_id,
    stock_value: action.payload.stock_value,
    stock_date: action.payload.stock_date,
    stock_contract_id: action.payload.contract_id
});

const updateStockValueReducer = (state: StockStatusState, action: PayloadAction<string | number>): StockStatusState => ({
    ...state,
    stock_value: action.payload
});

const updateStockStatusDataReducer = (state: StockStatusState, action: PayloadAction<null | Record<number, Record<number, StockData | string>>>): StockStatusState => ({
    ...state,
    stock_status_data: action.payload
});

const updateStockStatusDataRowLengthReducer = (state: StockStatusState, action: PayloadAction<number>): StockStatusState => ({
    ...state, stock_status_data_row_length: action.payload
});

const updateStockStatusDataColumnLengthReducer = (state: StockStatusState, action: PayloadAction<number>): StockStatusState => ({
    ...state, stock_status_data_column_length: action.payload
});

const updateRoomProductReducer = (state: StockStatusState, action: PayloadAction<RoomProduct[] | null>): StockStatusState => ({
    ...state,
    room_products: action.payload,
    room_products_loading: false
});

const loadRoomProductInfoReducer = (state: StockStatusState): StockStatusState => ({
    ...state, room_products_loading: true
});

const updateRoomProductInfoReducer = (state: StockStatusState, action: PayloadAction<RoomProductInfo | null>): StockStatusState => ({
    ...state, room_product_infos: action.payload, room_products_loading: false
});

const resetCloseSaleVariablesReducer = (state: StockStatusState): StockStatusState => ({
    ...state, closed_sale_start_date: null, closed_sale_accommodation_id: null, closed_sale_end_date: null, closed_sale_hover_date: null, closed_sale_room_id: null
});

const setEventSiteDateReducer = (state: StockStatusState, action: PayloadAction<null | EventSiteDate>): StockStatusState => ({
    ...state, event_site_date: action.payload
});

export const stockStatusSlice = createSlice({
    name: 'stockStatus',
    initialState,
    reducers: {
        noActiveContractForDate: noActiveContractForDateReducer,
        setNoActiveContract: setNoActiveContractReducer,
        setAccommodationList: setAccommodationListReducer,
        setAccommodationListLoading: setAccommodationListLoadingReducer,
        setContractList: setContractListReducer,
        setCurrentDay: setCurrentDayReducer,
        setCurrentMonth: setCurrentMonthReducer,
        setDateList: setDateListReducer,
        setEditMode: setEditModeReducer,
        setEndDate: setEndDateReducer,
        setRoomList: setRoomListReducer,
        setPriceList: setPriceListReducer,
        setStartDate: setStartDateReducer,
        setIsModalOpen: setIsModalOpenReducer,
        toggleDisplayMode: toggleDisplayModeReducer,
        toggleRefreshList: toggleRefreshListReducer,
        updateClosedHoverDate: updateClosedHoverDateReducer,
        updateClosedSale: updateClosedSaleReducer,
        updateRoomBlackOut: updateRoomBlackOutReducer,
        updatePrice: updatePriceReducer,
        updatePriceStock: updatePriceStockReducer,
        updatePriceValue: updatePriceValueReducer,
        updateRoomStock: updateRoomStockReducer,
        updateRoomStockWithoutClose: updateRoomStockWithoutCloseReducer,
        updateStock: updateStockReducer,
        updateStockValue: updateStockValueReducer,
        updateStockStatusData: updateStockStatusDataReducer,
        updateStockStatusDataRowLength: updateStockStatusDataRowLengthReducer,
        updateStockStatusDataColumnLength: updateStockStatusDataColumnLengthReducer,
        updateRoomProduct: updateRoomProductReducer,
        loadRoomProductInfo: loadRoomProductInfoReducer,
        updateRoomProductInfo: updateRoomProductInfoReducer,
        resetCloseSaleVariables: resetCloseSaleVariablesReducer,
        setEventSiteDate: setEventSiteDateReducer
    }
});

export const {
    noActiveContractForDate,
    setNoActiveContract,
    setAccommodationList,
    setAccommodationListLoading,
    setContractList,
    setCurrentDay,
    setCurrentMonth,
    setDateList,
    setEditMode,
    setEndDate,
    setRoomList,
    setPriceList,
    setStartDate,
    setIsModalOpen,
    toggleDisplayMode,
    toggleRefreshList,
    updateClosedHoverDate,
    updateClosedSale,
    updateRoomBlackOut,
    updatePriceStock,
    updatePrice,
    updatePriceValue,
    updateRoomStock,
    updateRoomStockWithoutClose,
    updateStock,
    updateStockValue,
    updateStockStatusData,
    updateStockStatusDataRowLength,
    updateStockStatusDataColumnLength,
    updateRoomProduct,
    loadRoomProductInfo,
    updateRoomProductInfo,
    resetCloseSaleVariables,
    setEventSiteDate
} = stockStatusSlice.actions;